import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Gift, ArrowRight, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { formatAUD, MEDIA } from "@/lib/media";
import { useStore } from "@/context/StoreContext";
import { WaveDivider, PawField, Paw } from "@/components/decor";
import { toast } from "sonner";

export default function BundleBuilder() {
  useSeo("Build-Your-Own Bundle — Chewie & Solo Gift Bundle",
    "Mix a book, a tee and a bandana into your own Chewie & Solo gift bundle at one happy set price.");
  const { addBundle } = useStore();
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState({});   // slotKey -> product
  const [variant, setVariant] = useState({});      // slotKey -> {size, colour}

  useEffect(() => {
    api.get("/bundle-builder").then(({ data }) => setConfig(data)).catch(() => setConfig(false));
  }, []);

  if (config === null) return <div className="max-w-7xl mx-auto px-4 py-24"><div className="h-64 bg-white/60 rounded-3xl animate-pulse" /></div>;
  if (config === false) return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display font-extrabold text-3xl text-[#19324A]">Bundle builder is taking a nap. 🐾</h1>
      <Link to="/shop" className="btn-primary mt-6 inline-flex">Back to Shop</Link>
    </div>
  );

  const allChosen = config.slots.every((s) => selected[s.key]);

  const pick = (slotKey, product) => {
    setSelected((s) => ({ ...s, [slotKey]: product }));
    setVariant((v) => ({ ...v, [slotKey]: { size: null, colour: null } }));
  };
  const setVar = (slotKey, field, val) => setVariant((v) => ({ ...v, [slotKey]: { ...v[slotKey], [field]: val } }));

  const validateVariants = () => {
    for (const s of config.slots) {
      const p = selected[s.key];
      const v = variant[s.key] || {};
      if ((p.sizes?.length || 0) > 0 && !v.size) { toast.error(`Choose a size for your ${s.label.toLowerCase()}.`); return false; }
      if ((p.colours?.length || 0) > 0 && !v.colour) { toast.error(`Choose a colour for your ${s.label.toLowerCase()}.`); return false; }
    }
    return true;
  };

  const addToBag = () => {
    if (!allChosen) { toast.error("Pick one from each step first."); return; }
    if (!validateVariants()) return;
    const components = config.slots.map((s) => {
      const p = selected[s.key];
      const v = variant[s.key] || {};
      return { product_id: p.id, name: p.name, slug: p.slug, image: (p.images || [])[0], size: v.size, colour: v.colour };
    });
    addBundle({ name: config.name, price: config.price, image: components[0].image, components });
    toast.success("Bundle added to your bag 🎁🐾");
    setSelected({}); setVariant({});
  };

  return (
    <div>
      <section className="relative bg-[#A78BFA]/20 py-14 sm:py-18 text-center overflow-hidden">
        <PawField color="#A78BFA" opacity={0.08} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="font-hand text-2xl text-[#FF6B9D]">Make it your own</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#19324A] mt-1 flex items-center justify-center gap-3"><Gift className="w-9 h-9 text-[#A78BFA]" />Build-Your-Own Bundle</h1>
          <p className="mt-4 text-[#19324A]/75 text-lg">{config.description}</p>
          <div className="mt-4 inline-flex items-center gap-2 bg-white rounded-full px-5 py-2 soft-shadow font-display font-bold text-[#19324A]">
            One set price: <span className="text-[#FF6B9D] text-xl">{formatAUD(config.price)}</span>
          </div>
        </div>
        <WaveDivider color="#FFF9EE" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[1fr_340px] gap-8">
        <div className="space-y-10">
          {config.slots.map((slot, idx) => (
            <div key={slot.key} data-testid={`bundle-slot-${slot.key}`}>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-9 h-9 rounded-full bg-[#FFD93D] flex items-center justify-center font-display font-bold text-[#19324A]">{idx + 1}</span>
                <h2 className="font-display font-extrabold text-2xl text-[#19324A]">{slot.label}</h2>
                {selected[slot.key] && <Check className="w-6 h-6 text-[#7ED957]" />}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {slot.products.map((p) => {
                  const active = selected[slot.key]?.id === p.id;
                  return (
                    <button key={p.id} onClick={() => pick(slot.key, p)} data-testid={`bundle-pick-${p.slug}`}
                      className={`text-left bg-white rounded-2xl overflow-hidden soft-shadow transition-all ${active ? "ring-4 ring-[#FF6B9D]" : "ring-2 ring-transparent hover:ring-[#FFD93D]"}`}>
                      <div className="relative aspect-square bg-[#FFF9EE]">
                        <img src={(p.images || [])[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
                        {active && <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#FF6B9D] text-white flex items-center justify-center"><Check className="w-4 h-4" /></span>}
                      </div>
                      <div className="p-3"><p className="font-display font-semibold text-sm text-[#19324A] line-clamp-2">{p.name}</p></div>
                    </button>
                  );
                })}
              </div>
              {selected[slot.key] && ((selected[slot.key].sizes?.length || 0) > 0 || (selected[slot.key].colours?.length || 0) > 0) && (
                <div className="mt-4 bg-white rounded-2xl p-4 soft-shadow flex flex-wrap gap-4">
                  {(selected[slot.key].colours?.length || 0) > 0 && (
                    <div><p className="text-xs font-semibold text-[#19324A]/60 mb-1">Colour</p><div className="flex flex-wrap gap-2">
                      {selected[slot.key].colours.map((c) => (
                        <button key={c} onClick={() => setVar(slot.key, "colour", c)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${variant[slot.key]?.colour === c ? "border-[#FF6B9D] bg-[#FF6B9D]/10" : "border-[#19324A]/15"}`}>{c}</button>
                      ))}</div></div>
                  )}
                  {(selected[slot.key].sizes?.length || 0) > 0 && (
                    <div><p className="text-xs font-semibold text-[#19324A]/60 mb-1">Size</p><div className="flex flex-wrap gap-2">
                      {selected[slot.key].sizes.map((sz) => (
                        <button key={sz} onClick={() => setVar(slot.key, "size", sz)} className={`px-3 py-1.5 rounded-xl text-sm font-semibold border-2 ${variant[slot.key]?.size === sz ? "border-[#FFD93D] bg-[#FFD93D]/30" : "border-[#19324A]/15"}`}>{sz}</button>
                      ))}</div></div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-24 self-start bg-white rounded-3xl p-6 soft-shadow h-fit">
          <h2 className="font-display font-bold text-xl text-[#19324A] mb-4">Your Bundle</h2>
          <div className="space-y-3">
            {config.slots.map((s) => (
              <div key={s.key} className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#FFF9EE] overflow-hidden flex items-center justify-center shrink-0">
                  {selected[s.key] ? <img src={(selected[s.key].images || [])[0]} alt="" className="w-full h-full object-cover" /> : <Paw className="w-6 h-6 text-[#19324A]/20" />}
                </div>
                <div className="min-w-0"><p className="text-xs text-[#19324A]/50">{s.label}</p><p className="font-semibold text-sm text-[#19324A] truncate">{selected[s.key]?.name || "Not chosen yet"}</p></div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#19324A]/10 mt-5 pt-4 flex justify-between font-display font-bold text-lg text-[#19324A]">
            <span>Bundle price</span><span>{formatAUD(config.price)}</span>
          </div>
          <button onClick={addToBag} disabled={!allChosen} className="btn-pink w-full mt-4 disabled:opacity-50" data-testid="bundle-add-btn">
            <ShoppingBag className="w-5 h-5" />{allChosen ? "Add Bundle to Bag" : "Pick one from each step"}
          </button>
        </div>
      </section>
    </div>
  );
}
